import { FieldErrors } from 'react-hook-form';
import { ConfirmAndPayFormData } from '../components/confirmAndPay/index';

/**
 * Helper function to get the first error field name from nested errors
 */
export const getFirstErrorField = (
  errors: FieldErrors<ConfirmAndPayFormData>
): string | null => {
  const getFirstError = (obj: any, prefix = ''): string | null => {
    for (const key in obj) {
      const value = obj[key];
      const fieldName = prefix ? `${prefix}.${key}` : key;

      if (value && typeof value === 'object') {
        if ('message' in value) {
          // This is an error object
          return fieldName;
        }
        // This is a nested object, recurse
        const nestedError = getFirstError(value, fieldName);
        if (nestedError) {
          return nestedError;
        }
      }
    }
    return null;
  };

  return getFirstError(errors);
};

/**
 * Helper function to scroll to error field
 * @param fieldName - The field name path (e.g., "businessLocationInfo.name")
 * @param setFocus - react-hook-form's setFocus function
 */
export const scrollToErrorField = (
  fieldName: string,
  setFocus: (name: any, options?: { shouldSelect?: boolean }) => void
): void => {
  // First, try to find the element in the DOM
  const findElement = (): HTMLElement | null => {
    // Try by full field name
    let element = document.querySelector(
      `[name="${fieldName}"]`
    ) as HTMLElement;

    if (!element) {
      // Try by last part of field name
      const fieldParts = fieldName.split('.');
      const lastPart = fieldParts[fieldParts.length - 1];
      element = document.querySelector(
        `input[name="${lastPart}"], textarea[name="${lastPart}"]`
      ) as HTMLElement;
    }

    if (!element) {
      // Find by error message (most reliable fallback)
      const errorMessages = document.querySelectorAll(
        '[class*="MuiFormHelperText"]'
      );
      for (const errorMsg of Array.from(errorMessages)) {
        const errorText = errorMsg.textContent || '';
        if (
          errorText.trim().length > 0 &&
          errorMsg.getBoundingClientRect().height > 0
        ) {
          const container = errorMsg.closest(
            '[class*="MuiBox"], [class*="Grid"], form'
          );
          if (container) {
            const input = container.querySelector(
              'input, textarea'
            ) as HTMLElement;
            if (input) {
              element = input;
              break;
            }
          }
        }
      }
    }

    return element;
  };

  // Try using react-hook-form's setFocus first
  try {
    setFocus(fieldName as any, { shouldSelect: false });
  } catch (e) {}

  // Always try to scroll manually as well (setFocus might focus but not scroll)
  setTimeout(() => {
    const element = findElement();
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });

      // Focus the element
      if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
        setTimeout(() => element.focus(), 100);
      } else {
        const nearestInput = element.querySelector(
          'input, textarea'
        ) as HTMLElement;
        if (nearestInput) {
          setTimeout(() => nearestInput.focus(), 100);
        }
      }
    }
  }, 200);
};

